import { useEffect, useMemo, useState, useRef } from 'react';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { JSXElementConstructor } from 'react';

import { programSelectors } from '../../../models/program';
import { getChannelById } from '../../../models/channels/selectors';
import ViewTrigger from '../../../components/view-trigger';

import ContentLayoutGallery from '../../../components/v2/content-layouts/content-layout-gallery';
import ContentLayoutRow from '../../../components/v2/content-layouts/content-layout-row';
import ContentLayoutGrid from '../../../components/v2/content-layouts/content-layout-grid';
import ContentLayoutHero from '../../../components/v2/content-layouts/content-layout-hero';
import { DEFAULT_SKIP_TO_CONTENT_ID } from '../../../components/skip-to-content/skip-to-content';
import withRerenderPrevent from '../../../common/with-rerender-prevent';
import {
  HomeFeedSection as HomeFeedSectionType,
  useHomeFeedSectionsQuery,
} from '../../../common/queries/use-home-feed-sections-query';
import { useContentFeedQuery } from '../../../common/queries/use-content-feed-query';
import {
  InitiativeTagResponse,
  useInitiativeTagsQuery,
} from '../../../common/queries/use-initiative-tags-query';
import { Program } from '../../../models/program/types';
import { Content } from '../../../models/content/types';
import { Channel } from '../../../models/channels/types';
import { RootPatronState } from '../../../common/use-patron-selector';

/**
 * Props for the HomeFeedSection component
 */
type HomeFeedSectionProps = {
  item: HomeFeedSectionType;
  className: string;
  updateSectionContents: (
    order: number,
    newContents: Content[],
    newMaxContents: number
  ) => void;
  contents: Content[];
  initiativeTags: InitiativeTagResponse[];
};

/**
 * Max display contents configuration
 */
type MaxDisplayContents = {
  hero: number;
  grid: number;
  gallery: number;
  row: number;
};

/**
 * HomeFeedSection component that renders a single feed section
 */
const HomeFeedSection = ({
  item,
  className,
  updateSectionContents,
  contents,
  initiativeTags,
}: HomeFeedSectionProps): JSX.Element | null => {
  const {
    filter_id: filterId,
    display_type: displayType,
    feed,
    order,
    min_contents: minContents,
  } = item.attributes;

  const { t } = useTranslation();

  const manualFetch = order > 1;
  const channel = useSelector((state: RootPatronState) =>
    getChannelById(state, filterId)
  ) as Channel | undefined;

  // Find matching initiative tag by converting filterId to string for comparison
  const initiative = initiativeTags.find(
    (tag) => tag.id === String(filterId)
  )?.attributes;

  const filter = (): string | undefined => {
    if (feed === 'community') return 'community_images';
    if (feed === 'channel') return undefined;
    return feed;
  };

  const displayTitle = (): string => {
    if (feed === 'channel') return channel?.name || '';
    if (feed === 'initiative') return initiative?.name || '';
    return t(`landing.${feed}`, feed);
  };

  const displayDescription = (): string | undefined => {
    if (feed === 'channel') return channel?.description;
    if (feed === 'initiative') return undefined;
    return t(`landing.${feed}_description`);
  };

  const getSectionId = (): string => {
    if (feed === 'channel' && channel) return `channel-${channel.id}`;
    if (feed === 'initiative' && initiative) {
      return `initiative-${initiative.id}`;
    }
    return feed;
  };

  const viewAllText =
    feed === 'featured' || feed === 'trending' || feed === 'latest'
      ? t(`landing.see_all_${feed}`, '')
      : `${t('landing.see_all')} ${displayTitle()}`;

  const maxDisplayContents = useMemo<MaxDisplayContents>(
    () => ({
      hero: 4,
      grid: 6,
      gallery: 9,
      row: 3,
    }),
    []
  );

  const viewAllUrl = (): string => {
    if (feed === 'channel') return `/channels/${filterId}`;
    if (feed === 'initiative') return `/initiatives/${filterId}`;
    return `/feed/${feed}`;
  };

  const perPage =
    Math.max(...Object.values(maxDisplayContents)) * order +
    maxDisplayContents[displayType];

  // Create a ref to track if we've processed this data already
  const processedDataRef = useRef(false);

  // Use React Query to fetch content
  const {
    data: feedItems = [],
    isLoading,
    isSuccess: loaded,
    refetch: fetchFeed,
  } = useContentFeedQuery({
    filter: filter(),
    perPage: perPage,
    channelId: feed === 'channel' ? filterId.toString() : undefined,
    initiativeId: feed === 'initiative' ? filterId.toString() : undefined,
    includeTypes: displayType === 'gallery' ? ['image'] : undefined,
  });

  // Use effect to handle content updates with ref to prevent infinite loops
  useEffect(() => {
    if (loaded && feedItems.length > 0 && !processedDataRef.current) {
      updateSectionContents(order, feedItems, maxDisplayContents[displayType]);
      processedDataRef.current = true;
    }
  }, [
    loaded,
    feedItems,
    order,
    maxDisplayContents,
    displayType,
    updateSectionContents,
  ]);

  // Reset the ref when key dependencies change that would require a new data update
  useEffect(() => {
    processedDataRef.current = false;
  }, [order, displayType, feed, filterId]);

  const shouldDisplay = (): boolean => {
    // The home feed component assumes an array of content with length > 1
    // Adding defensive check for uninitialized data coming from server
    let result = contents.length >= (minContents || 1);

    if (feed === 'initiative') {
      result = result && initiative !== undefined && initiative.is_active;
    }

    if (feed === 'channel') {
      result = result && channel !== undefined;
    }

    return result;
  };

  // Eventually we need to clean up these class names
  // Some of these don't seem to have any CSS defined against them
  const classNames = {
    hero: 'home__featured-v2',
    grid: 'home__digests',
    gallery: 'home__community-v2',
    row: 'home__row-v2',
  };

  if (manualFetch && !loaded) {
    return <ViewTrigger offset="200px" onInview={fetchFeed} />;
  }

  if (!shouldDisplay()) {
    return null;
  }

  // Build base common props for all feed components
  const baseProps = {
    id: getSectionId(),
    title: displayTitle(),
    description: displayDescription(),
    seeAllText: viewAllText,
    viewAllText,
    feedUrl: viewAllUrl(),
    contents: contents,
    analyticsData: { location: `home_${feed}` },
    isTopic: feed === 'channel',
  };

  return (
    <section
      id={DEFAULT_SKIP_TO_CONTENT_ID}
      className={cx(classNames[displayType], className)}
      aria-labelledby={`${baseProps.id}--title`}
      aria-describedby={
        baseProps.description ? `${baseProps.id}--description` : undefined
      }
    >
      <div className="container">
        {displayType === 'gallery' && <ContentLayoutGallery {...baseProps} />}
        {displayType === 'hero' && <ContentLayoutHero {...baseProps} />}
        {displayType === 'grid' && (
          <ContentLayoutGrid
            {...baseProps}
            minLength={minContents || 1}
            maxLength={6}
            isFetching={isLoading}
          />
        )}
        {displayType === 'row' && (
          <ContentLayoutRow
            {...baseProps}
            minLength={minContents || 1}
            isFetching={isLoading}
          />
        )}
      </div>
    </section>
  );
};

/**
 * Deduplicates content arrays based on content IDs
 *
 * @param contentsArr - Array of content arrays to deduplicate
 * @param lengthArr - Array of max lengths for each content array
 * @returns Deduplicated array of content arrays
 */
const dedupContents = (
  contentsArr: Content[][],
  lengthArr: number[]
): Content[][] => {
  const flattenContentIds = (contents: Content[][]) =>
    Array.prototype.concat
      .apply([] as Content[], contents)
      .map((c: Content) => c.id);

  return contentsArr.reduce((arr, contents, idx) => {
    const curIds = flattenContentIds(arr);
    const uniqContents = contents
      .filter((c) => !curIds.includes(c.id))
      .slice(0, lengthArr[idx]);

    return [...arr, uniqContents];
  }, [] as Content[][]);
};

/**
 * Props for the HomeFeeds component
 */
type HomeFeedsProps = {
  inCaseYouMissedItEnabled?: boolean;
  fromTheCommunityEnabled?: boolean;
  forYouFeedEnabled?: boolean;
  program?: Program;
};

/**
 * HomeFeeds is the main component that displays all feed sections on the home page
 */
const HomeFeeds = ({
  inCaseYouMissedItEnabled = false,
  fromTheCommunityEnabled = false,
  forYouFeedEnabled = false,
}: HomeFeedsProps): JSX.Element | null => {
  // Use React Query to fetch feed sections
  const { data: feedSections = [], isLoading: isFetchingFeedSections } =
    useHomeFeedSectionsQuery();

  const numFeeds = feedSections.length;
  const [sectionsContents, setSectionsContents] = useState<Content[][]>(
    Array(numFeeds).fill([])
  );
  const [maxContents, setMaxContents] = useState<number[]>(
    Array(numFeeds).fill(0)
  );
  const uniqContents = useMemo(() => {
    return dedupContents(sectionsContents, maxContents);
  }, [sectionsContents, maxContents]);

  // Extract filter IDs for initiative tags query
  const filterIds = useMemo(() => {
    return feedSections
      .map((section) => section.attributes?.filter_id)
      .filter(Boolean)
      .map(String);
  }, [feedSections]);

  // Use React Query to fetch initiative tags
  const { data: initiativeTagsData = [] } = useInitiativeTagsQuery(
    filterIds.length > 0 ? filterIds : undefined
  );

  /**
   * Updates the content and max content arrays for a specific section
   *
   * @param order - The order/position of the section to update
   * @param newContents - The new content items for the section
   * @param newMaxContents - The new max content count for the section
   */
  const updateSectionContents = (
    order: number,
    newContents: Content[],
    newMaxContents: number
  ): void => {
    setMaxContents((maxContentsOld) =>
      maxContentsOld.map((length, index) => {
        return index === order ? newMaxContents : length;
      })
    );

    setSectionsContents((sectionsContentsOld) =>
      sectionsContentsOld.map((contents, index) => {
        return index === order ? newContents : contents;
      })
    );
  };

  /**
   * Determines if a specific feed type should be displayed
   *
   * @param feed - The feed type to check
   * @returns Whether the feed should be displayed
   */
  const display = (feed: string): boolean => {
    if (feed === 'community') return fromTheCommunityEnabled;
    if (feed === 'icymi') return inCaseYouMissedItEnabled;
    if (feed === 'for_you') return forYouFeedEnabled;
    return true;
  };

  // Update state arrays when the number of feeds changes
  useEffect(() => {
    if (numFeeds > 0) {
      setSectionsContents(Array(numFeeds).fill([]));
      setMaxContents(Array(numFeeds).fill(0));
    }
  }, [numFeeds]);

  if (isFetchingFeedSections) {
    return null;
  }

  return (
    <div className="home__feed-sections">
      {feedSections.map((feed_section, idx) =>
        display(feed_section.attributes.feed) ? (
          <HomeFeedSection
            item={feed_section}
            className={cx('home_row', {
              home__first: idx === 0,
            })}
            updateSectionContents={updateSectionContents}
            contents={uniqContents[idx] || []}
            initiativeTags={initiativeTagsData}
            key={idx}
          />
        ) : null
      )}
    </div>
  );
};

/**
 * Maps Redux state to component props
 */
const mapStateToProps = (state: RootPatronState) => ({
  inCaseYouMissedItEnabled:
    programSelectors.getProgramInCaseYouMissedItEnabled(state),
  fromTheCommunityEnabled:
    programSelectors.getProgramFromTheCommunityEnabled(state),
  forYouFeedEnabled: programSelectors.getForYouFeedEnabled(state),
  program: programSelectors.getProgram(state),
});

export default compose<JSXElementConstructor<HomeFeedsProps>>(
  connect(mapStateToProps, null),
  withRerenderPrevent
)(HomeFeeds);
